<!-- Loading indicator for URL measurements - placed as a sibling to ensure maximum visibility -->
<div *ngIf="loadingUrlMeasurements" class="url-measurements-loading-indicator">
  <div class="spinner-border text-light" role="status">
    <span class="visually-hidden">{{ "Loading measurements..." | translate }}</span>
  </div>
  <div class="loading-text mt-2">{{ "Loading measurements..." | translate }}</div>
</div>

<!-- Help content template -->
<ng-template #helpContent let-offcanvasRef>
  <div class="offcanvas-header">
    <h3 class="mb-0">{{ "Measuring tool help" | translate }}</h3>
    <button (click)="offcanvasRef.dismiss()" class="btn-close" aria-label="Close" type="button"></button>
  </div>
  <div class="offcanvas-body">
    <div class="measuring-help" style="font-size: 0.9rem">
      <h5>{{ "Getting started" | translate }}</h5>
      <p>{{ "The measuring tool allows you to measure distances and sizes of objects in the image." | translate }}</p>

      <h5>{{ "Creating measurements" | translate }}</h5>
      <ul>
        <li>{{ "Click once to set your starting point" | translate }}</li>
        <li>{{ "Click a second time to set the end point and complete the measurement" | translate }}</li>
        <li>{{ "Alternatively, click and drag to create a measurement in one motion" | translate }}</li>
        <li>
          {{
            "For images with plate-solving data, the tool will show distance in astronomical units (arcseconds, arcminutes, or degrees)"
              | translate
          }}
        </li>
        <li>{{ "For images without plate-solving data, distances will be shown in pixels" | translate }}</li>
      </ul>

      <h5>{{ "Adjusting measurements" | translate }}</h5>
      <ul>
        <li>{{ "Click and drag any point to reposition it" | translate }}</li>
        <li>{{ "Click and drag the middle of a measurement to move the entire line" | translate }}</li>
        <li>{{ "Use the circle or rectangle buttons to change how a measurement is visualized" | translate }}</li>
      </ul>

      <h5>{{ "Saving and sharing" | translate }}</h5>
      <ul>
        <li>{{ "Click the share button to generate a URL that includes your measurements" | translate }}</li>
        <li>{{ "Click the bookmark button to view saved measurements (requires login)" | translate }}</li>
        <li>{{ "Saved measurements can be loaded on any image with plate-solving data" | translate }}</li>
      </ul>

      <!-- Add FAQ section here -->
      <h5>{{ "FAQ" | translate }}</h5>
      <ul>
        <li>
          {{ "Why are the measurement labels sometimes slightly different when I share a link or reload?" | translate }}
          <br />
          <small>{{
            "Due to sub-pixel calculations during plate-solving and rendering variations based on the size of the image viewer on your screen, the exact pixel coordinates corresponding to a measurement might differ slightly between sessions or devices. This can lead to minor variations in the displayed angular separation (e.g., arcseconds or arcminutes)."
              | translate
          }}</small>
        </li>
      </ul>
    </div>
  </div>
</ng-template>

<div
  *ngIf="active"
  [class.hidden-during-resize]="isWindowResizing"
  class="measuring-tool-container pointer-events-all"
  data-ignore-swipe-down="true"
>
  <!-- Overlay for capturing clicks/touches and drags during measuring mode -->
  <!-- Put this first so other elements can be on top of it -->
  <div
    (click)="handleMeasurementClick($event)"
    (mousedown)="handleMeasurementMouseDown($event)"
    (touchend)="handleMeasurementTouchEnd($event)"
    (touchmove)="handleMeasurementTouchMove($event)"
    (touchstart)="handleMeasurementTouchStart($event)"
    class="measure-click-overlay measurement-tool-canvas"
    data-tutorial-id="measurement-tool-canvas"
  ></div>

  <!-- Start and end markers -->
  <div
    *ngIf="measureStartPoint"
    [class.dragging]="isDraggingPoint === 'start'"
    [style.left.px]="measureStartPoint.x"
    [style.top.px]="measureStartPoint.y"
    (mousedown)="handlePointDragStart($event, 'start')"
    (touchstart)="handlePointTouchStart($event, 'start')"
    class="measure-marker start-marker"
  >
    <div class="marker-dot"></div>
    <!-- Start point coordinates with extended directional positioning -->
    <div
      *ngIf="!measureEndPoint && measureStartPoint.ra !== null && measureStartPoint.dec !== null"
      [class.outside-boundaries]="measureStartPoint.outsideBoundaries"
      class="marker-coordinates initial-position visible"
      style="position: absolute; top: -24px; left: -24px; transform: translateX(-50%)"
    >
      <small>{{
        measureStartPoint.outsideBoundaries
          ? "n/a"
          : (measureStartPoint.ra | formatCoordinatesCompact: measureStartPoint.dec)
      }}</small>
    </div>

    <!-- Once both points are selected, calculate perpendicular position for labels -->
    <div
      *ngIf="measureEndPoint && measureStartPoint.ra !== null && measureStartPoint.dec !== null"
      [class.dragging]="isDraggingPoint === 'start'"
      [class.outside-boundaries]="measureStartPoint.outsideBoundaries"
      [style.left.px]="(measureStartPoint | calculateLabelPosition: measureEndPoint : 'start').x"
      [style.top.px]="(measureStartPoint | calculateLabelPosition: measureEndPoint : 'start').y"
      (mousedown)="handlePointDragStart($event, 'start')"
      (touchstart)="handlePointTouchStart($event, 'start')"
      class="marker-coordinates visible draggable-label"
    >
      <small>{{
        measureStartPoint.outsideBoundaries
          ? "n/a"
          : (measureStartPoint.ra | formatCoordinatesCompact: measureStartPoint.dec)
      }}</small>
    </div>
  </div>

  <div
    *ngIf="measureEndPoint"
    [class.dragging]="isDraggingPoint === 'end'"
    [style.left.px]="measureEndPoint.x"
    [style.top.px]="measureEndPoint.y"
    (mousedown)="handlePointDragStart($event, 'end')"
    (touchstart)="handlePointTouchStart($event, 'end')"
    class="measure-marker end-marker"
  >
    <div class="marker-dot"></div>
    <!-- End point coordinates perpendicular to line -->
    <div
      *ngIf="measureEndPoint.ra !== null && measureEndPoint.dec !== null"
      [class.dragging]="isDraggingPoint === 'end'"
      [class.outside-boundaries]="measureEndPoint.outsideBoundaries"
      [style.left.px]="(measureStartPoint | calculateLabelPosition: measureEndPoint : 'end').x"
      [style.top.px]="(measureStartPoint | calculateLabelPosition: measureEndPoint : 'end').y"
      (mousedown)="handlePointDragStart($event, 'end')"
      (touchstart)="handlePointTouchStart($event, 'end')"
      class="marker-coordinates visible draggable-label"
    >
      <small>{{
        measureEndPoint.outsideBoundaries ? "n/a" : (measureEndPoint.ra | formatCoordinatesCompact: measureEndPoint.dec)
      }}</small>
    </div>
  </div>

  <!-- Previous lines -->
  <svg [attr.height]="windowHeight" [attr.width]="windowWidth" class="measure-line-container">
    <!-- Previous measurements -->
    <g *ngFor="let measurement of previousMeasurements; let i = index; trackBy: trackByMeasurement">
      <line
        [attr.x1]="measurement.startX"
        [attr.x2]="measurement.endX"
        [attr.y1]="measurement.startY"
        [attr.y2]="measurement.endY"
        class="measure-line previous-line"
      ></line>
    </g>

    <!-- Current measurement line -->
    <line
      *ngIf="!measureEndPoint && measureStartPoint && mouseX !== null && mouseY !== null"
      [attr.x1]="measureStartPoint.x"
      [attr.x2]="mouseX"
      [attr.y1]="measureStartPoint.y"
      [attr.y2]="mouseY"
      class="measure-line measuring"
    ></line>
    <line
      *ngIf="measureEndPoint"
      [attr.x1]="measureStartPoint.x"
      [attr.x2]="measureEndPoint.x"
      [attr.y1]="measureStartPoint.y"
      [attr.y2]="measureEndPoint.y"
      class="measure-line current-line"
    ></line>

    <!-- Visual indicator for original position when dragging current measurement points -->
    <g *ngIf="isDraggingPoint === 'start' && measureStartPoint">
      <circle
        [attr.cx]="dragStartX || measureStartPoint.x"
        [attr.cy]="dragStartY || measureStartPoint.y"
        class="measure-point original-position"
        r="5"
      ></circle>
      <line
        [attr.x1]="dragStartX || measureStartPoint.x"
        [attr.x2]="measureStartPoint.x"
        [attr.y1]="dragStartY || measureStartPoint.y"
        [attr.y2]="measureStartPoint.y"
        class="measure-line drag-indicator"
      ></line>
    </g>

    <g *ngIf="isDraggingPoint === 'end' && measureEndPoint">
      <circle
        [attr.cx]="dragStartX || measureEndPoint.x"
        [attr.cy]="dragStartY || measureEndPoint.y"
        class="measure-point original-position"
        r="5"
      ></circle>
      <line
        [attr.x1]="dragStartX || measureEndPoint.x"
        [attr.x2]="measureEndPoint.x"
        [attr.y1]="dragStartY || measureEndPoint.y"
        [attr.y2]="measureEndPoint.y"
        class="measure-line drag-indicator"
      ></line>
    </g>

    <!-- Visual indicators for dragging previous measurement points -->
    <g *ngFor="let measurement of previousMeasurements; let i = index; trackBy: trackByMeasurement">
      <!-- Start point drag indicator -->
      <g *ngIf="isDraggingPoint === 'prevStart-' + i">
        <circle
          [attr.cx]="dragStartX || measurement.startX"
          [attr.cy]="dragStartY || measurement.startY"
          class="measure-point original-position"
          r="5"
        ></circle>
        <line
          [attr.x1]="dragStartX || measurement.startX"
          [attr.x2]="measurement.startX"
          [attr.y1]="dragStartY || measurement.startY"
          [attr.y2]="measurement.startY"
          class="measure-line drag-indicator"
        ></line>
      </g>

      <!-- End point drag indicator -->
      <g *ngIf="isDraggingPoint === 'prevEnd-' + i">
        <circle
          [attr.cx]="dragStartX || measurement.endX"
          [attr.cy]="dragStartY || measurement.endY"
          class="measure-point original-position"
          r="5"
        ></circle>
        <line
          [attr.x1]="dragStartX || measurement.endX"
          [attr.x2]="measurement.endX"
          [attr.y1]="dragStartY || measurement.endY"
          [attr.y2]="measurement.endY"
          class="measure-line drag-indicator"
        ></line>
      </g>
    </g>
  </svg>

  <!-- Previous measurement points - DIV-based draggable markers -->
  <div *ngFor="let measurement of previousMeasurements; let i = index; trackBy: trackByMeasurement">
    <!-- Start point marker -->
    <div
      [class.dragging]="isDraggingPoint === 'prevStart-' + i"
      [style.left.px]="measurement.startX"
      [style.top.px]="measurement.startY"
      (mousedown)="handlePreviousMeasurementDrag($event, i, 'start')"
      (touchstart)="handlePreviousMeasurementTouchDrag($event, i, 'start')"
      class="measure-marker previous-start-marker"
    >
      <div class="marker-dot"></div>
    </div>

    <!-- End point marker -->
    <div
      [class.dragging]="isDraggingPoint === 'prevEnd-' + i"
      [style.left.px]="measurement.endX"
      [style.top.px]="measurement.endY"
      (mousedown)="handlePreviousMeasurementDrag($event, i, 'end')"
      (touchstart)="handlePreviousMeasurementTouchDrag($event, i, 'end')"
      class="measure-marker previous-end-marker"
    >
      <div class="marker-dot"></div>
    </div>

    <!-- Circle drawing for previous measurement, if available -->
    <svg
      *ngIf="measurement.showCircle"
      [attr.height]="windowHeight"
      [attr.width]="windowWidth"
      [class.isDragging]="isDraggingPoint === 'prevShape-' + i"
      class="measure-shape-container"
    >
      <!-- Semi-transparent overlay for the area outside the circle -->
      <defs>
        <mask id="circle-mask-{{ i }}">
          <rect fill="white" height="100%" width="100%" x="0" y="0"></rect>
          <circle
            [attr.cx]="measurement.startX | getMidpoint: measurement.endX"
            [attr.cy]="measurement.startY | getMidpoint: measurement.endY"
            [attr.r]="
              (measurement.startX | calculateDistance: measurement.startY : measurement.endX : measurement.endY) / 2
            "
            fill="black"
          ></circle>
        </mask>
      </defs>

      <!-- This rect will darken everything except the masked circle area -->
      <rect
        [attr.mask]="'url(#circle-mask-' + i + ')'"
        class="shape-overlay"
        height="100%"
        width="100%"
        x="0"
        y="0"
      ></rect>

      <!-- The circle outline -->
      <circle
        [attr.cx]="measurement.startX | getMidpoint: measurement.endX"
        [attr.cy]="measurement.startY | getMidpoint: measurement.endY"
        [attr.r]="
          (measurement.startX | calculateDistance: measurement.startY : measurement.endX : measurement.endY) / 2
        "
        [class.outside-boundaries]="measurement.outOfBounds"
        class="measure-shape circle"
      ></circle>
    </svg>

    <!-- Rectangle drawing for previous measurement, if available -->
    <svg
      *ngIf="measurement.showRectangle"
      [attr.height]="windowHeight"
      [attr.width]="windowWidth"
      [class.isDragging]="isDraggingPoint === 'prevShape-' + i"
      class="measure-shape-container"
    >
      <!-- Semi-transparent overlay for the area outside the rectangle -->
      <defs>
        <mask id="rectangle-mask-{{ i }}">
          <rect fill="white" height="100%" width="100%" x="0" y="0"></rect>
          <g>
            <rect
              [attr.height]="measurement.endY - measurement.startY | mathAbs"
              [attr.width]="measurement.endX - measurement.startX | mathAbs"
              [attr.x]="measurement.startX | mathMin: measurement.endX"
              [attr.y]="measurement.startY | mathMin: measurement.endY"
              fill="black"
            ></rect>
          </g>
        </mask>
      </defs>

      <!-- This rect will darken everything except the masked rectangle area -->
      <rect
        [attr.mask]="'url(#rectangle-mask-' + i + ')'"
        class="shape-overlay"
        height="100%"
        width="100%"
        x="0"
        y="0"
      ></rect>

      <g>
        <!-- Rectangle outline (this must come before the dimension lines so it doesn't cover them) -->
        <rect
          [attr.height]="measurement.endY - measurement.startY | mathAbs"
          [attr.width]="measurement.endX - measurement.startX | mathAbs"
          [attr.x]="measurement.startX | mathMin: measurement.endX"
          [attr.y]="measurement.startY | mathMin: measurement.endY"
          [class.outside-boundaries]="measurement.outOfBounds"
          class="measure-shape rectangle"
        ></rect>

        <!-- Width dimension label (inside rotated group) -->
        <g class="dimension-label width-label">
          <!-- Width line -->
          <line
            [attr.x1]="measurement.startX | mathMin: measurement.endX"
            [attr.x2]="measurement.startX | mathMax: measurement.endX"
            [attr.y1]="(measurement.startY | mathMax: measurement.endY) + 15"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) + 15"
            class="dimension-line"
          ></line>

          <!-- Dimension arrows -->
          <line
            [attr.x1]="measurement.startX | mathMin: measurement.endX"
            [attr.x2]="(measurement.startX | mathMin: measurement.endX) + 5"
            [attr.y1]="(measurement.startY | mathMax: measurement.endY) + 15"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) + 10"
            class="dimension-arrow"
          ></line>
          <line
            [attr.x1]="measurement.startX | mathMin: measurement.endX"
            [attr.x2]="(measurement.startX | mathMin: measurement.endX) + 5"
            [attr.y1]="(measurement.startY | mathMax: measurement.endY) + 15"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) + 20"
            class="dimension-arrow"
          ></line>

          <line
            [attr.x1]="measurement.startX | mathMax: measurement.endX"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) - 5"
            [attr.y1]="(measurement.startY | mathMax: measurement.endY) + 15"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) + 10"
            class="dimension-arrow"
          ></line>
          <line
            [attr.x1]="measurement.startX | mathMax: measurement.endX"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) - 5"
            [attr.y1]="(measurement.startY | mathMax: measurement.endY) + 15"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) + 20"
            class="dimension-arrow"
          ></line>

          <!-- Width text -->
          <text
            [attr.dominant-baseline]="'central'"
            [attr.x]="
              measurement.startX
                | mathMin: measurement.endX
                | getMidpoint: (measurement.startX | mathMax: measurement.endX)
            "
            [attr.y]="checkHorizontalLabelPosition((measurement.startY | mathMax: measurement.endY) + 30, measurement)"
            class="dimension-text"
            text-anchor="middle"
          >
            {{ measurement.distanceX || "" }}
          </text>
        </g>

        <!-- Height dimension label (inside rotated group) -->
        <g class="dimension-label height-label">
          <!-- Height line -->
          <line
            [attr.x1]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.y1]="measurement.startY | mathMin: measurement.endY"
            [attr.y2]="measurement.startY | mathMax: measurement.endY"
            class="dimension-line"
          ></line>

          <!-- Dimension arrows -->
          <line
            [attr.x1]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) + 15"
            [attr.y1]="measurement.startY | mathMin: measurement.endY"
            [attr.y2]="(measurement.startY | mathMin: measurement.endY) + 5"
            class="dimension-arrow"
          ></line>
          <line
            [attr.x1]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) + 25"
            [attr.y1]="measurement.startY | mathMin: measurement.endY"
            [attr.y2]="(measurement.startY | mathMin: measurement.endY) + 5"
            class="dimension-arrow"
          ></line>

          <line
            [attr.x1]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) + 15"
            [attr.y1]="measurement.startY | mathMax: measurement.endY"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) - 5"
            class="dimension-arrow"
          ></line>
          <line
            [attr.x1]="(measurement.startX | mathMax: measurement.endX) + 20"
            [attr.x2]="(measurement.startX | mathMax: measurement.endX) + 25"
            [attr.y1]="measurement.startY | mathMax: measurement.endY"
            [attr.y2]="(measurement.startY | mathMax: measurement.endY) - 5"
            class="dimension-arrow"
          ></line>

          <!-- Height text - adjust position to avoid overlap with measurement box -->
          <text
            [attr.dominant-baseline]="'central'"
            [attr.x]="(measurement.startX | mathMax: measurement.endX) + 35"
            [attr.y]="
              checkVerticalLabelPosition(
                measurement.startY
                  | mathMin: measurement.endY
                  | getMidpoint: (measurement.startY | mathMax: measurement.endY),
                measurement
              )
            "
            class="dimension-text"
            text-anchor="start"
          >
            {{ measurement.distanceY || "" }}
          </text>
        </g>
      </g>
    </svg>
  </div>

  <!-- Distance labels - show previous measurements -->
  <div
    *ngFor="let measurement of previousMeasurements; let i = index; trackBy: trackByMeasurement"
    [class.outside-boundaries]="measurement.outOfBounds"
    [style.left.px]="measurement.midX"
    [style.top.px]="measurement.midY"
    (mousedown)="
      handleShapeDragStart($event, i, measurement.showCircle ? 'circle' : 'rectangle'); $event.preventDefault()
    "
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    (touchstart)="handleShapeTouchStart($event, i, measurement.showCircle ? 'circle' : 'rectangle')"
    class="measure-distance previous-measurement"
  >
    <div class="measurement-content">
      <!-- Display name if it exists -->
      <div *ngIf="measurement.name" class="measurement-name">
        {{ measurement.name }}
      </div>

      <!-- Display the distance measurement -->
      <span class="measurement-text" data-tutorial-id="measurement-distance-box">
        {{ measurement.distance }}
      </span>

      <!-- Display notes if they exist -->
      <div *ngIf="measurement.notes" class="measurement-notes">
        <span class="notes-content">{{ measurement.notes }}</span>
        <a
          *ngIf="measurement.notes"
          (click)="openFullNotesModal($event, measurement.notes)"
          (mousedown)="$event.stopPropagation()"
          (pointerdown)="$event.stopPropagation()"
          class="see-more-link"
          href="#"
        >
          {{ "See more" | translate }}
        </a>
      </div>
    </div>

    <div class="measurement-actions">
      <!-- Distance is already shown in the measurement-content div -->

      <!-- Shape toggles: circle and rectangle -->
      <button
        [class.active]="measurement.showCircle"
        (click)="toggleCircle($event, i)"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="toggleCircle($event, i)"
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action circle-button"
        data-tutorial-id="circle-button"
        type="button"
      >
        <fa-icon [ngbTooltip]="'Draw circle' | translate" container="body" icon="circle"></fa-icon>
      </button>

      <button
        [class.active]="measurement.showRectangle"
        (click)="toggleRectangle($event, i)"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="toggleRectangle($event, i)"
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action rectangle-button"
        data-tutorial-id="rectangle-button"
        type="button"
      >
        <fa-icon [ngbTooltip]="'Draw rectangle' | translate" container="body" icon="square"></fa-icon>
      </button>

      <!-- Separator between drawing tools and action buttons -->
      <div class="toolbar-separator"></div>

      <!-- Export button -->
      <button
        [class.disabled]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)"
        [style.opacity]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix) ? '0.5' : '1'"
        [style.pointer-events]="'auto'"
        (click)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openExportMeasurementModal($event, measurement)
            : $event.stopPropagation()
        "
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openExportMeasurementModal($event, measurement)
            : $event.stopPropagation()
        "
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action export-button"
        data-tutorial-id="export-button"
        type="button"
      >
        <fa-icon
          [ngbTooltip]="
            !(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)
              ? ('Exporting coordinates requires valid advanced plate-solving data' | translate)
              : ('Export coordinates' | translate)
          "
          container="body"
          icon="file-export"
        ></fa-icon>
      </button>

      <!-- Save button -->
      <button
        *ngIf="currentUser$ | async"
        [class.disabled]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)"
        [style.opacity]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix) ? '0.5' : '1'"
        [style.pointer-events]="'auto'"
        (click)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? savePreviousMeasurement($event, i)
            : $event.stopPropagation()
        "
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? savePreviousMeasurement($event, i)
            : $event.stopPropagation()
        "
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action save-button"
        data-tutorial-id="save-measurement-button"
        type="button"
      >
        <fa-icon
          [ngbTooltip]="
            !(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)
              ? ('Saving measurements requires valid advanced plate-solving data' | translate)
              : ('Save measurement' | translate)
          "
          container="body"
          icon="save"
        ></fa-icon>
      </button>

      <!-- Delete button -->
      <button
        (click)="deleteMeasurement($event, i)"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="deleteMeasurement($event, i)"
        (touchstart)="$event.stopPropagation()"
        class="btn-delete-measurement delete-button"
        type="button"
      >
        <fa-icon [ngbTooltip]="'Delete measurement' | translate" container="body" icon="times"></fa-icon>
      </button>
    </div>

    <!-- Include boundary warning template -->
    <ng-container *ngTemplateOutlet="boundaryWarningTpl; context: { $implicit: measurement }"></ng-container>
  </div>

  <!-- Current measurement circle -->
  <svg
    *ngIf="showCurrentCircle && measureStartPoint && measureEndPoint && measurementData"
    [attr.height]="windowHeight"
    [attr.width]="windowWidth"
    [class.isDragging]="isDraggingPoint === 'currentShape'"
    class="measure-shape-container"
  >
    <!-- Semi-transparent overlay for the area outside the circle -->
    <defs>
      <mask id="current-circle-mask">
        <rect fill="white" height="100%" width="100%" x="0" y="0"></rect>
        <circle
          [attr.cx]="measureStartPoint.x | getMidpoint: measureEndPoint.x"
          [attr.cy]="measureStartPoint.y | getMidpoint: measureEndPoint.y"
          [attr.r]="
            (measureStartPoint.x | calculateDistance: measureStartPoint.y : measureEndPoint.x : measureEndPoint.y) / 2
          "
          fill="black"
        ></circle>
      </mask>
    </defs>

    <!-- This rect will darken everything except the masked circle area -->
    <rect class="shape-overlay" height="100%" mask="url(#current-circle-mask)" width="100%" x="0" y="0"></rect>

    <!-- The circle outline -->
    <circle
      [attr.cx]="measureStartPoint.x | getMidpoint: measureEndPoint.x"
      [attr.cy]="measureStartPoint.y | getMidpoint: measureEndPoint.y"
      [attr.r]="
        (measureStartPoint.x | calculateDistance: measureStartPoint.y : measureEndPoint.x : measureEndPoint.y) / 2
      "
      [class.outside-boundaries]="isCurrentMeasurementOutsideBoundaries"
      class="measure-shape circle current"
    ></circle>
  </svg>

  <!-- Current measurement rectangle -->
  <svg
    *ngIf="showCurrentRectangle && measureStartPoint && measureEndPoint && measurementData"
    [attr.height]="windowHeight"
    [attr.width]="windowWidth"
    [class.isDragging]="isDraggingPoint === 'currentShape'"
    class="measure-shape-container"
  >
    <!-- Semi-transparent overlay for the area outside the rectangle -->
    <defs>
      <mask id="current-rectangle-mask">
        <rect fill="white" height="100%" width="100%" x="0" y="0"></rect>
        <g>
          <rect
            [attr.height]="measureEndPoint.y - measureStartPoint.y | mathAbs"
            [attr.width]="measureEndPoint.x - measureStartPoint.x | mathAbs"
            [attr.x]="measureStartPoint.x | mathMin: measureEndPoint.x"
            [attr.y]="measureStartPoint.y | mathMin: measureEndPoint.y"
            fill="black"
          ></rect>
        </g>
      </mask>
    </defs>

    <!-- This rect will darken everything except the masked rectangle area -->
    <rect class="shape-overlay" height="100%" mask="url(#current-rectangle-mask)" width="100%" x="0" y="0"></rect>

    <g>
      <!-- Rectangle outline (this must come before the dimension lines so it doesn't cover them) -->
      <rect
        [attr.height]="measureEndPoint.y - measureStartPoint.y | mathAbs"
        [attr.width]="measureEndPoint.x - measureStartPoint.x | mathAbs"
        [attr.x]="measureStartPoint.x | mathMin: measureEndPoint.x"
        [attr.y]="measureStartPoint.y | mathMin: measureEndPoint.y"
        [class.outside-boundaries]="isCurrentMeasurementOutsideBoundaries"
        class="measure-shape rectangle current"
      ></rect>

      <!-- Width dimension label -->
      <g class="dimension-label width-label">
        <!-- Width line -->
        <line
          [attr.x1]="Math.min(measureStartPoint.x, measureEndPoint.x)"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x)"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          class="dimension-line current"
        ></line>

        <!-- Dimension arrows -->
        <line
          [attr.x1]="Math.min(measureStartPoint.x, measureEndPoint.x)"
          [attr.x2]="Math.min(measureStartPoint.x, measureEndPoint.x) + 5"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) + 10"
          class="dimension-arrow current"
        ></line>
        <line
          [attr.x1]="Math.min(measureStartPoint.x, measureEndPoint.x)"
          [attr.x2]="Math.min(measureStartPoint.x, measureEndPoint.x) + 5"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) + 20"
          class="dimension-arrow current"
        ></line>

        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x)"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) - 5"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) + 10"
          class="dimension-arrow current"
        ></line>
        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x)"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) - 5"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y) + 15"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) + 20"
          class="dimension-arrow current"
        ></line>

        <!-- Width text -->
        <text
          [attr.dominant-baseline]="'central'"
          [attr.x]="
            (Math.min(measureStartPoint.x, measureEndPoint.x) + Math.max(measureStartPoint.x, measureEndPoint.x)) / 2
          "
          [attr.y]="checkHorizontalLabelPosition(Math.max(measureStartPoint.y, measureEndPoint.y) + 30)"
          class="dimension-text current"
          text-anchor="middle"
        >
          {{ measurementData?.distanceX || "" }}
        </text>
      </g>

      <!-- Height dimension label -->
      <g class="dimension-label height-label">
        <!-- Height line -->
        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.y1]="Math.min(measureStartPoint.y, measureEndPoint.y)"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y)"
          class="dimension-line current"
        ></line>

        <!-- Dimension arrows -->
        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) + 15"
          [attr.y1]="Math.min(measureStartPoint.y, measureEndPoint.y)"
          [attr.y2]="Math.min(measureStartPoint.y, measureEndPoint.y) + 5"
          class="dimension-arrow current"
        ></line>
        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) + 25"
          [attr.y1]="Math.min(measureStartPoint.y, measureEndPoint.y)"
          [attr.y2]="Math.min(measureStartPoint.y, measureEndPoint.y) + 5"
          class="dimension-arrow current"
        ></line>

        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) + 15"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y)"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) - 5"
          class="dimension-arrow current"
        ></line>
        <line
          [attr.x1]="Math.max(measureStartPoint.x, measureEndPoint.x) + 20"
          [attr.x2]="Math.max(measureStartPoint.x, measureEndPoint.x) + 25"
          [attr.y1]="Math.max(measureStartPoint.y, measureEndPoint.y)"
          [attr.y2]="Math.max(measureStartPoint.y, measureEndPoint.y) - 5"
          class="dimension-arrow current"
        ></line>

        <!-- Height text - adjust position to avoid overlap with measurement box -->
        <text
          [attr.dominant-baseline]="'central'"
          [attr.x]="Math.max(measureStartPoint.x, measureEndPoint.x) + 35"
          [attr.y]="
            checkVerticalLabelPosition(
              (Math.min(measureStartPoint.y, measureEndPoint.y) + Math.max(measureStartPoint.y, measureEndPoint.y)) / 2
            )
          "
          class="dimension-text current"
          text-anchor="start"
        >
          {{ measurementData?.distanceY || "" }}
        </text>
      </g>
    </g>
  </svg>

  <!-- Current measurement display -->
  <div
    *ngIf="measureDistance && measureStartPoint && measureEndPoint"
    [class.outside-boundaries]="isCurrentMeasurementOutsideBoundaries"
    [style.left.px]="(measureStartPoint.x + measureEndPoint.x) / 2"
    [style.top.px]="(measureStartPoint.y + measureEndPoint.y) / 2"
    (mousedown)="
      handleCurrentShapeDragStart($event, showCurrentCircle ? 'circle' : 'rectangle'); $event.preventDefault()
    "
    (mouseenter)="setMouseOverUIElement(true)"
    (mouseleave)="setMouseOverUIElement(false)"
    (touchstart)="handleCurrentShapeTouchStart($event, showCurrentCircle ? 'circle' : 'rectangle')"
    class="measure-distance current-measurement"
  >
    <div class="measurement-content">
      <!-- Display name if it exists -->
      <div *ngIf="newMeasurementName" class="measurement-name">
        {{ newMeasurementName }}
      </div>

      <!-- Display the distance measurement -->
      <span class="measurement-text">
        {{ measureDistance }}
      </span>

      <!-- Display notes if they exist -->
      <div *ngIf="saveMeasurementNotes" class="measurement-notes">
        <span class="notes-content">{{ saveMeasurementNotes }}</span>
        <a
          *ngIf="saveMeasurementNotes"
          (click)="openFullNotesModal($event, saveMeasurementNotes)"
          (mousedown)="$event.stopPropagation()"
          (pointerdown)="$event.stopPropagation()"
          class="see-more-link"
          href="#"
        >
          {{ "See more" | translate }}
        </a>
      </div>
    </div>

    <div class="measurement-actions">
      <!-- Distance is already shown in the measurement-content div -->

      <!-- Shape toggles: circle and rectangle -->
      <button
        [class.active]="showCurrentCircle"
        (click)="toggleCurrentCircle($event)"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="toggleCurrentCircle($event)"
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action circle-button"
        type="button"
      >
        <fa-icon [ngbTooltip]="'Draw circle' | translate" container="body" icon="circle"></fa-icon>
      </button>

      <button
        [class.active]="showCurrentRectangle"
        (click)="toggleCurrentRectangle($event)"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="toggleCurrentRectangle($event)"
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action rectangle-button"
        type="button"
      >
        <fa-icon [ngbTooltip]="'Draw rectangle' | translate" container="body" icon="square"></fa-icon>
      </button>

      <!-- Separator between drawing tools and action buttons -->
      <div class="toolbar-separator"></div>

      <!-- Export button -->
      <button
        [class.disabled]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)"
        [style.opacity]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix) ? '0.5' : '1'"
        [style.pointer-events]="'auto'"
        (click)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openExportMeasurementModal($event, {
                startX: measureStartPoint.x,
                startY: measureStartPoint.y,
                endX: measureEndPoint.x,
                endY: measureEndPoint.y,
                midX: 0,
                midY: 0,
                distance: measureDistance,
                timestamp: UtilsService.getHighResTime(),
                startRa: measureStartPoint.ra,
                startDec: measureStartPoint.dec,
                endRa: measureEndPoint.ra,
                endDec: measureEndPoint.dec,
                startLabelX: 0,
                startLabelY: 0,
                endLabelX: 0,
                endLabelY: 0,
                showCircle: showCurrentCircle,
                showRectangle: showCurrentRectangle
              })
            : $event.stopPropagation()
        "
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openExportMeasurementModal($event, {
                startX: measureStartPoint.x,
                startY: measureStartPoint.y,
                endX: measureEndPoint.x,
                endY: measureEndPoint.y,
                midX: 0,
                midY: 0,
                distance: measureDistance,
                timestamp: UtilsService.getHighResTime(),
                startRa: measureStartPoint.ra,
                startDec: measureStartPoint.dec,
                endRa: measureEndPoint.ra,
                endDec: measureEndPoint.dec,
                startLabelX: 0,
                startLabelY: 0,
                endLabelX: 0,
                endLabelY: 0,
                showCircle: showCurrentCircle,
                showRectangle: showCurrentRectangle
              })
            : $event.stopPropagation()
        "
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action export-button"
        type="button"
      >
        <fa-icon
          [ngbTooltip]="
            !(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)
              ? ('Exporting coordinates requires valid advanced plate-solving data' | translate)
              : ('Export coordinates' | translate)
          "
          container="body"
          icon="file-export"
        ></fa-icon>
      </button>

      <!-- Save button -->
      <button
        *ngIf="currentUser$ | async"
        [class.disabled]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)"
        [style.opacity]="!(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix) ? '0.5' : '1'"
        [style.pointer-events]="'auto'"
        (click)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openSaveCurrentMeasurement($event)
            : $event.stopPropagation()
        "
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="$event.stopPropagation()"
        (touchend)="
          advancedSolutionMatrix && advancedSolutionMatrix.raMatrix
            ? openSaveCurrentMeasurement($event)
            : $event.stopPropagation()
        "
        (touchstart)="$event.stopPropagation()"
        class="btn-shape-action save-button save-measurements-button"
        type="button"
      >
        <fa-icon
          [ngbTooltip]="
            !(advancedSolutionMatrix && advancedSolutionMatrix.raMatrix)
              ? ('Saving measurements requires valid advanced plate-solving data' | translate)
              : ('Save measurement' | translate)
          "
          container="body"
          icon="save"
        ></fa-icon>
      </button>
    </div>

    <!-- Include boundary warning template for current measurement -->
    <ng-container
      *ngTemplateOutlet="boundaryWarningTpl; context: { $implicit: { isCurrentMeasurement: true } }"
    ></ng-container>
  </div>

  <!-- Coordinate labels for previous measurements -->
  <div *ngFor="let measurement of previousMeasurements; let i = index; trackBy: trackByMeasurement">
    <!-- Start point coordinates with perpendicular positioning -->
    <div
      *ngIf="measurement.startRa !== null && measurement.startDec !== null"
      [class.dragging]="isDraggingPoint === 'prevStart-' + i"
      [class.outside-boundaries]="measurement.startOutsideBoundaries"
      [style.left.px]="measurement.startLabelX"
      [style.top.px]="measurement.startLabelY"
      (mousedown)="handlePreviousMeasurementDrag($event, i, 'start')"
      (touchstart)="handlePreviousMeasurementTouchDrag($event, i, 'start')"
      class="measure-coord-label visible draggable-label"
    >
      <small>{{
        measurement.startOutsideBoundaries
          ? "n/a"
          : (measurement.startRa | formatCoordinatesCompact: measurement.startDec)
      }}</small>
    </div>

    <!-- End point coordinates with perpendicular positioning -->
    <div
      *ngIf="measurement.endRa !== null && measurement.endDec !== null"
      [class.dragging]="isDraggingPoint === 'prevEnd-' + i"
      [class.outside-boundaries]="measurement.endOutsideBoundaries"
      [style.left.px]="measurement.endLabelX"
      [style.top.px]="measurement.endLabelY"
      (mousedown)="handlePreviousMeasurementDrag($event, i, 'end')"
      (touchstart)="handlePreviousMeasurementTouchDrag($event, i, 'end')"
      class="measure-coord-label visible draggable-label"
    >
      <small>{{
        measurement.endOutsideBoundaries ? "n/a" : (measurement.endRa | formatCoordinatesCompact: measurement.endDec)
      }}</small>
    </div>
  </div>

  <!-- Fixed toolbar at top center - matching annotation toolbar style -->
  <div class="fixed-top-toolbar">
    <div class="toolbar-buttons">
      <!-- Help button -->
      <button
        [ngbTooltip]="'Help' | translate"
        (click)="showHelp()"
        (touchend)="showHelp()"
        (touchstart)="$event.stopPropagation()"
        class="icon-button help-button"
        container="body"
        data-tutorial-id="measurement-help"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'question']"></fa-icon>
      </button>

      <!-- Separator after help button -->
      <div class="toolbar-separator"></div>

      <!-- Saved measurements button -->
      <button
        *ngIf="currentUser$ | async"
        [disabled]="!advancedSolutionMatrix"
        [ngbTooltip]="
          advancedSolutionMatrix
            ? ('Saved measurements' | translate)
            : ('Saved measurements require advanced plate-solving data' | translate)
        "
        (click)="advancedSolutionMatrix ? toggleSavedMeasurements($event) : null"
        (touchend)="advancedSolutionMatrix ? toggleSavedMeasurements($event) : null"
        (touchstart)="$event.stopPropagation()"
        class="icon-button"
        container="body"
        data-tutorial-id="measurement-bookmarks"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'bookmark']"></fa-icon>
      </button>

      <!-- Share measurements button -->
      <button
        [disabled]="previousMeasurements.length === 0"
        [ngbTooltip]="'Share measurements' | translate"
        (click)="previousMeasurements.length > 0 ? shareMeasurements() : null"
        (touchend)="previousMeasurements.length > 0 ? shareMeasurements() : null"
        (touchstart)="$event.stopPropagation()"
        class="icon-button"
        container="body"
        data-tutorial-id="measurement-share"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'share-alt']"></fa-icon>
      </button>

      <!-- Exit/Clear section with a visual separator -->
      <div class="toolbar-separator"></div>

      <!-- Clear all measurements button -->
      <button
        [disabled]="previousMeasurements.length === 0"
        [ngbTooltip]="'Clear all measurements' | translate"
        (click)="clearAllMeasurements()"
        (touchend)="previousMeasurements.length > 0 ? clearAllMeasurements() : null"
        (touchstart)="$event.stopPropagation()"
        class="icon-button"
        container="body"
        data-tutorial-id="measurement-clear"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'eraser']"></fa-icon>
      </button>

      <!-- Exit button -->
      <button
        [ngbTooltip]="'Exit measuring tool' | translate"
        (click)="exitMeasuring()"
        (touchend)="exitMeasuring()"
        (touchstart)="$event.stopPropagation()"
        class="icon-button exit-button"
        container="body"
        data-tutorial-id="measurement-exit"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'times']"></fa-icon>
      </button>
    </div>
  </div>

  <!-- Saved Measurements Panel -->
  <div *ngIf="showSavedMeasurements" (wheel)="$event.stopPropagation()" class="saved-measurements-panel">
    <div class="panel-header">
      <h5>{{ "Saved measurements" | translate }}</h5>
      <button (click)="toggleSavedMeasurements($event)" class="btn btn-sm btn-link text-muted">
        <fa-icon icon="times"></fa-icon>
      </button>
    </div>

    <div (wheel)="$event.stopPropagation()" class="panel-body">
      <!-- Current measurement - save option -->
      <div *ngIf="measureDistance" class="current-measurement-save">
        <div class="mb-2">
          <h6>{{ "Current Measurement" | translate }}</h6>
          <p>{{ measureDistance }}</p>
        </div>
        <div *ngIf="currentUser$ | async">
          <!-- Name input -->
          <div class="input-group input-group-sm mb-2">
            <input
              class="form-control"
              [(ngModel)]="newMeasurementName"
              placeholder="{{ 'Measurement name' | translate }}"
              type="text"
            />
          </div>

          <!-- Notes input -->
          <div class="input-group input-group-sm mb-2">
            <textarea
              class="form-control"
              [(ngModel)]="saveMeasurementNotes"
              placeholder="{{ 'Measurement notes (optional)' | translate }}"
              rows="2"
            ></textarea>
          </div>

          <!-- Save button -->
          <div class="input-group input-group-sm mb-2">
            <button
              [class.disabled]="!advancedSolutionMatrix"
              [ngbTooltip]="
                !advancedSolutionMatrix
                  ? ('Saving measurements requires advanced plate-solving data' | translate)
                  : ('Save measurement' | translate)
              "
              [style.pointer-events]="'auto'"
              (click)="advancedSolutionMatrix ? saveMeasurement() : null"
              class="btn btn-primary"
              container="body"
            >
              <fa-icon class="me-1" icon="save"></fa-icon>
              {{ "Save" | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- Saved measurements list -->
      <div (wheel)="$event.stopPropagation()" class="saved-measurements-list">
        <p *ngIf="savedMeasurements.length === 0" class="text-muted">
          {{ "No saved measurements yet." | translate }}
        </p>

        <div *ngIf="!advancedSolutionMatrix && savedMeasurements.length > 0" class="alert alert-warning mb-2">
          <small>{{
            "This image lacks advanced plate-solving data required to use saved measurements. You cannot load measurements on this image."
              | translate
          }}</small>
        </div>

        <div *ngFor="let preset of savedMeasurements; let i = index; trackBy: trackByPreset" class="measurement-item">
          <div class="measurement-details">
            <div class="measurement-name">{{ preset.name }}</div>
            <div *ngIf="preset.notes && preset.notes.trim()" class="measurement-notes">{{ preset.notes }}</div>
            <div *ngIf="preset.created" class="measurement-date">{{ preset.created | date: "short" }}</div>
          </div>
          <div *ngIf="currentUser$ | async" class="measurement-actions">
            <button
              [class.disabled]="!advancedSolutionMatrix || loadingMeasurementId === preset.id"
              [class.loading]="loadingMeasurementId === preset.id"
              [style.pointer-events]="'auto'"
              (click)="loadMeasurementWrapper(preset)"
              class="btn btn-sm btn-outline-light"
            >
              <fa-icon
                *ngIf="loadingMeasurementId !== preset.id"
                [ngbTooltip]="
                  !advancedSolutionMatrix
                    ? ('Loading measurements requires advanced plate-solving data' | translate)
                    : ('Load measurement' | translate)
                "
                container="body"
                icon="arrow-right"
              ></fa-icon>
              <fa-icon *ngIf="loadingMeasurementId === preset.id" [spin]="true" icon="spinner"></fa-icon>
            </button>
            <button (click)="deleteSavedMeasurement(i)" class="btn btn-sm btn-outline-danger">
              <fa-icon
                [ngbTooltip]="'Delete saved measurement' | translate"
                container="body"
                icon="trash-alt"
              ></fa-icon>
            </button>
          </div>
        </div>

        <!-- Delete All button -->
        <div *ngIf="savedMeasurements.length > 0" class="delete-all-container">
          <button (click)="confirmDeleteAllMeasurements($event)" class="btn btn-danger w-100 mt-2" type="button">
            <fa-icon class="me-1" icon="trash-alt"></fa-icon>
            {{ "Delete all measurements" | translate }}
          </button>
        </div>
      </div>

      <!-- Note about measurements -->
      <div class="precision-note">
        <small>{{ "Measurements have sub-pixel precision and field distortion limitations." | translate }}</small>
      </div>
    </div>
  </div>

  <!-- Boundary warning template -->
  <ng-template #boundaryWarningTpl let-data>
    <div
      *ngIf="data.isCurrentMeasurement ? isCurrentMeasurementOutsideBoundaries : data.outOfBounds"
      class="boundary-warning"
    >
      {{ "Coordinates will not work outside the image's boundaries" | translate }}
    </div>
  </ng-template>
</div>
